import React from 'react';
import { PublicClientApplication } from '@azure/msal-browser';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import api from '@/utils/api';
import { msalConfig, loginRequest, isMicrosoftConfigured, showMicrosoftButton } from '@/authConfig';

let msalInstance = null;
if (isMicrosoftConfigured()) {
  try {
    msalInstance = new PublicClientApplication(msalConfig);
  } catch (error) {
    console.error('Failed to initialize MSAL:', error);
  }
}

const GoogleIcon = () => (
  <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const MicrosoftIcon = () => (
  <svg className="mr-2 h-5 w-5" viewBox="0 0 23 23">
    <path fill="#f3f3f3" d="M0 0h23v23H0z"/>
    <path fill="#f35325" d="M1 1h10v10H1z"/>
    <path fill="#81bc06" d="M12 1h10v10H12z"/>
    <path fill="#05a6f0" d="M1 12h10v10H1z"/>
    <path fill="#ffba08" d="M12 12h10v10H12z"/>
  </svg>
);

const handleGoogleLogin = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + '/auth';
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}&app_name=${encodeURIComponent('SAFER-I')}`;
};

const microsoftErrorMessage = (error) => {
  if (error.errorCode === 'user_cancelled') return null;
  return error.response?.data?.detail || 'Microsoft login failed. Please try again.';
};

export default function SocialLoginButtons({ loading, setLoading, onAuthenticated }) {
  const handleMicrosoftLogin = async () => {
    if (!msalInstance) {
      toast.error('Microsoft authentication is not configured. Please contact administrator.');
      return;
    }
    setLoading(true);
    try {
      await msalInstance.initialize();
      const response = await msalInstance.loginPopup(loginRequest);
      if (response?.account) {
        const apiResponse = await api.post('/auth/microsoft/token', {
          email: response.account.username,
          name: response.account.name,
        }, { withCredentials: true });
        onAuthenticated(apiResponse.data);
      }
    } catch (error) {
      const msg = microsoftErrorMessage(error);
      if (msg) toast.error(msg); else toast.info('Login cancelled');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="mt-4 relative">
        <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border" /></div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-slate-50 px-2 text-muted-foreground">Or continue with</span>
        </div>
      </div>
      <div className="mt-4 space-y-2">
        <Button type="button" variant="outline" className="w-full h-12 font-semibold" onClick={handleGoogleLogin} disabled={loading} data-testid="google-login-button">
          <GoogleIcon /> Sign in with Google
        </Button>
        {showMicrosoftButton() && (
          <Button type="button" variant="outline" className="w-full h-12 font-semibold" onClick={handleMicrosoftLogin} disabled={loading} data-testid="microsoft-login-button">
            <MicrosoftIcon /> Sign in with Microsoft
          </Button>
        )}
      </div>
    </>
  );
}
